import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from 'platform/custom/store/store';
import { Icon, Tooltip } from '@ohif/ui';
import { getReportTemplatesApi } from '../../rtk-apis/reports/getReportTemplates';
import { clearState } from '../../store/slices/reports/getReportTemplates';
import BackButton from '../../components/BackButton';
import QuickDistributePopup from '../../components/QuickDistributePopup';
import { utils } from '@ohif/core';
import { getHospitalId, getToken } from '../../../../platform/custom/Utils/authUtils';
import { REPORTS } from '../../../../platform/custom/constant';
import { getCurrentReportApi } from '../../rtk-apis/reports/getCurrentReport';
import { clearState as clearCurrentReport } from '../../store/slices/reports/getCurrentReport';
import { changeReportStatus } from '../../rtk-apis/reports/changeReportStatus';
import { clearChangeReportStatus } from '../../store/slices/reports/changeReportStatus';
import { dismissReminder } from '../../rtk-apis/reports/dismissReminder';
import { sendReportNotification } from '../../rtk-apis/reports/sendReportNotification';
import {
  canChangeReportStatus,
  canCreateReport,
  canRegenerateReport,
} from '../../../../platform/custom/Utils/reportPermissions';
import { Alert, Snackbar } from '@mui/material';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import RegenerateReportDialog from '../../components/RegenerateReportDialog';
import { useRegenerateReport } from '../../Utils/useRegenerateReport';
import PatientAttachmentsModal from '../patients/components/patientAttachments/PatientAttachmentsModal';
import { resolveExistingReportId } from '../../Utils/reportNavigation';
import { useClinicFeatures } from '../../Utils/useClinicFeatures';

const { formatDate, formatTime } = utils;

// Inline SVG glyphs for the slim action bar (no @mui/icons-material — not a
// declared dependency of this monorepo).
const glyphProps = {
  width: 15,
  height: 15,
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.4,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const DistributionGlyph = () => (
  <svg {...glyphProps}>
    <path d="M14.5 1.5 7 9M14.5 1.5l-4.5 13-2.5-5.5L2 6.5l12.5-5z" />
  </svg>
);

const AttachGlyph = () => (
  <svg {...glyphProps}>
    <path d="M13.5 7.5 8 13a3.5 3.5 0 0 1-5-5l6-6a2.5 2.5 0 0 1 3.5 3.5L6.7 11.3a1.2 1.2 0 0 1-1.7-1.7L10 4.5" />
  </svg>
);

const BellGlyph = () => (
  <svg {...glyphProps}>
    <path d="M3.5 11h9c-1-1-1.2-2-1.2-4a3.3 3.3 0 1 0-6.6 0c0 2-.2 3-1.2 4z" />
    <path d="M6.8 13.2a1.3 1.3 0 0 0 2.4 0" />
  </svg>
);

const ExpandGlyph = () => (
  <svg {...glyphProps}>
    <path d="M9.5 2h4.5v4.5M6.5 14H2V9.5M14 2 9 7M2 14l5-5" />
  </svg>
);

const PencilGlyph = () => (
  <svg {...glyphProps}>
    <path d="m10.8 2.6 2.6 2.6L5 13.6l-3.3.7.7-3.3 8.4-8.4z" />
  </svg>
);

// Signature: hairline divider carrying a single ECG pulse — the one decorative
// element of the header, drawn from the product's own subject.
const EcgDivider = () => (
  <div
    aria-hidden
    style={{ display: 'flex', alignItems: 'center', height: 12, marginTop: 2 }}
  >
    <div style={{ width: 22, height: 1, background: 'rgba(122,160,199,0.3)' }} />
    <svg
      width="76"
      height="16"
      viewBox="0 0 76 16"
      fill="none"
      style={{ flexShrink: 0 }}
    >
      <path
        d="M0 8h16l4-3 5 6 7-11 6 13 4-5h34"
        stroke="#2FB4BE"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    <div style={{ flex: 1, height: 1, background: 'rgba(122,160,199,0.3)' }} />
  </div>
);

const DetailsTile = ({
  studyData,
  setSelectedReportTemplate,
  setOpenReportPanel,
  isStudyMetaDataFetched,
  isLoading,
  toggleFullScreen,
  onBeforeRegenerate,
  // Opening a saved report waits only for the SR series; isLoading also covers images.
  isViewLoading = isLoading,
}: any) => {
  const reportId = studyData?.reportId;
  const navigate = useNavigate();
  const { dicomViewerEnabled } = useClinicFeatures();
  const params = useParams();
  const location = useLocation();
  const ReportListStudyInstanceUID = location.state?.studyInstanceUID;
  const currentStudyUID = params?.studyInstanceUIDs || ReportListStudyInstanceUID;

  const hasHydrated = useRef(false);
  const [patientNameOverride, setPatientNameOverride] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameSaveError, setNameSaveError] = useState('');
  const [currentReport, setCurrentReport] = useState({});
  const resolvedReportId = resolveExistingReportId(reportId, currentReport, currentStudyUID);
  const [templateList, setTemplateList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState('Generate Report');
  const templateDropdown = useRef(null);
  const userRole = sessionStorage.getItem('role') || localStorage.getItem('role');
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [quickDistributeOpen, setQuickDistributeOpen] = useState(false);
  const [isAttachmentsOpen, setIsAttachmentsOpen] = useState(false);
  // Why View Report declined to open, shown to the clinician instead of nothing.
  const [viewError, setViewError] = useState<string | null>(null);
  const [isNotifModalOpen, setIsNotifModalOpen] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');
  const [isSendingNotif, setIsSendingNotif] = useState(false);
  const [notifResult, setNotifResult] = useState<{ msg: string; ok: boolean } | null>(null);
  const statusDropdownRef = useRef(null);
  const isButtonDisabled = !canCreateReport(userRole) && selectedItem === 'Generate Report';

  const { requestRegenerate, dialogProps: regenerateDialogProps } = useRegenerateReport({
    onBeforeRegenerate,
    onRegenerated: template => {
      setSelectedReportTemplate(template);
      setOpenReportPanel(true);
    },
  });
  const canRegenerate =
    Boolean(resolvedReportId) &&
    (currentReport as any)?._id === resolvedReportId &&
    canRegenerateReport(
      userRole,
      (currentReport as any)?.status,
      (currentReport as any)?.reportAssignedToId,
      sessionStorage.getItem('userId') || localStorage.getItem('userId')
    );

  const {
    status: getCurrentReportStatus,
    result: getCurrentReportResult,
    msg: getCurrentReportMsg,
    loading: getCurrentReportLoading,
  } = useSelector((state: RootState) => state.getCurrentReportResponse);

  // Generate Report drafts the report in the background, so for a moment the report is
  // being created but this header still knows nothing about it. Showing "Generate Report"
  // in that window invited a second click (and a second draft); flipping straight to
  // "View Report" only after both calls land looked like the button changed by itself.
  // Both async steps are covered: createReport, then the current-report refetch that tells
  // this header the report exists. Either failing simply returns the Generate control.
  const { loading: createReportLoading } = useSelector(
    (state: RootState) => state.createReportResponse
  );
  const isPreparingReport = !resolvedReportId && (createReportLoading || getCurrentReportLoading);

  const statusOptions = [
    { value: 'Draft', label: 'Draft' },
    { value: 'Preliminary', label: 'Preliminary' },
    { value: 'Finalized', label: 'Finalized' },
  ];

  useEffect(() => {
    if (!currentStudyUID) {
      return;
    }
    const hospitalId = getHospitalId();
    if (!hospitalId) {
      return;
    }
    fetch(REPORTS.GET_PATIENT_NAME_OVERRIDES, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ studyInstanceUIDs: [currentStudyUID], hospitalId }),
    })
      .then(r => r.json())
      .then(d => {
        const name = d?.result?.[currentStudyUID];
        if (name) {
          setPatientNameOverride(name);
        }
      })
      .catch(() => {});
  }, [currentStudyUID]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (templateDropdown.current && !templateDropdown.current.contains(event.target)) {
        setIsOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setIsStatusDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    hasHydrated.current = true;
    setOpenReportPanel(false);
    setSelectedReportTemplate(null);
    setCurrentReport({});
    dispatch(clearCurrentReport());
  }, [currentStudyUID]);

  const dispatch = useDispatch<AppDispatch>();
  const { status, result, msg, loading } = useSelector(
    (state: RootState) => state.getReportTemplatesResponse
  );

  // Fetch templates on component mount
  const handleGetReportTemplate = () => {
    dispatch(
      getReportTemplatesApi({
        search: '',
        templates_per_page: 12,
      })
    );
  };

  const handleSelect = (item: any) => {
    console.log('🚀 ~ handleSelect ~ item:', item);
    setSelectedReportTemplate({
      item,
      hospitalHeader: templateList?.hospitalHeader,
      hospitalLogo: templateList?.hospitalLogo,
    });
    setSelectedItem(item?.templateName);
    setIsOpen(!isOpen);
    setSearchTerm('');
    setOpenReportPanel(true);
  };

  useEffect(() => {
    if (status === 'SUCCESS') {
      setTemplateList(result);
    } else if (status === 'ERROR') {
      console.error('Error fetching report templates:', msg);
    }
    dispatch(clearState());
  }, [status]);

  // View Report: fetch fresh and build the panel DIRECTLY from the awaited
  // response. The old flow round-tripped through the redux slice + effects,
  // which rebuilt from stale cached state on click (before the fresh response
  // landed) and then ignored the fresh data — reopening after minimize showed
  // a stale or blank body.
  const handleView = async () => {
    const hospitalId = getHospitalId();
    const studyInstanceUID = params?.studyInstanceUIDs || ReportListStudyInstanceUID;
    const action: any = await dispatch(
      getCurrentReportApi({
        studyInstanceUID: studyInstanceUID,
        hospitalId: hospitalId,
      })
    );
    const data = action?.payload?.data;

    /**
     * The guard below refuses to open a blank or stale panel. It must never refuse
     * SILENTLY: a clinical button that does nothing and says nothing reads as a broken
     * application, and it hid a real data loss (a Preliminary report whose stored body
     * had gone missing) until a cardiologist reported it by hand. Every rejection path
     * now names its reason and logs the identifiers needed to find the record.
     */
    if (!data) {
      console.warn('[view-report] no report for study', studyInstanceUID);
      setViewError('No report exists for this study yet. Use Generate to create one.');
      return;
    }
    if (data.studyInstanceUID !== currentStudyUID) {
      console.warn('[view-report] response is for a different study', {
        got: data.studyInstanceUID,
        expected: currentStudyUID,
      });
      setViewError('Could not load this study’s report. Please refresh and try again.');
      return;
    }
    if (!data.templateHTML) {
      console.error('[view-report] report record has no body', {
        reportId: data._id,
        studyInstanceUID: data.studyInstanceUID,
        status: data.status,
      });
      setViewError(
        `This report record has no content (ref ${data._id}). Please contact support before re-reporting.`
      );
      return;
    }

    // Every rejection is handled above; from here the body is known usable.
    setCurrentReport(data);
    if (data.pendingReview) {
      dispatch(dismissReminder({ reportId: data._id }));
    }
    setSelectedReportTemplate({
      item: {
        templateHTML: data?.templateHTML,
        reportType: data?.reportType,
      },
      hospitalHeader: data?.hospitalHeader,
      hospitalLogo: data?.hospitalLogo,
      colorCode: Array.isArray(data?.colorCode) ? data.colorCode[0] : data?.colorCode,
      from: 'view_report',
      _id: data?._id,
      sharedClipsId: data?.sharedClipsId,
      sharedClipsUrl: data?.sharedClipsUrl,
      sharedClipsName: data?.sharedClipsName,
      attachedClips: data?.attachedClips,
      reportAssignedToId: data?.reportAssignedToId,
      reportAssignedTo: data?.reportAssignedTo,
      status: data?.status,
    });
    setOpenReportPanel(true);
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      setIsUpdatingStatus(true);
      setIsStatusDropdownOpen(false);
      const payload = { reportId: currentReport._id, reportStatus: newStatus };
      await dispatch(changeReportStatus(payload)).unwrap();
      setCurrentReport(prev => ({ ...prev, status: newStatus }));
      setSelectedReportTemplate(prev => (prev ? { ...prev, status: newStatus } : prev));
      dispatch(clearChangeReportStatus());
      if (newStatus === 'Finalized') {
        setQuickDistributeOpen(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Determine report existence from the study UID itself. Route state is only
  // a navigation hint and may contain the raw DICOM study without reportId
  // (notably when the patient name is clicked from Studies).
  useEffect(() => {
    const hospitalId = getHospitalId();
    if (!currentStudyUID || !hospitalId) {
      return;
    }
    dispatch(
      getCurrentReportApi({
        studyInstanceUID: currentStudyUID,
        hospitalId,
      })
    );
  }, [dispatch, currentStudyUID]);

  // Passive cache refresh only (used by the study-UID fetch) — the
  // View Report panel is built exclusively inside handleView from the awaited
  // response, never from this effect.
  useEffect(() => {
    if (getCurrentReportStatus === 'SUCCESS') {
      const data = getCurrentReportResult?.data;
      if (data && data.studyInstanceUID === currentStudyUID) {
        setCurrentReport(data);
        if (data.pendingReview) {
          dispatch(dismissReminder({ reportId: data._id }));
        }
      }
    }
    return () => {
      dispatch(clearCurrentReport());
    };
  }, [getCurrentReportStatus, currentStudyUID, getCurrentReportResult]);

  useEffect(() => {
    if (studyData?.defaultTemp) {
      setSelectedItem(studyData?.defaultTemp?.item?.templateName);
    }
  }, [studyData]);

  const startEditName = () => {
    setNameInput(patientNameOverride || studyData?.patientName || '');
    setNameSaveError('');
    setIsEditingName(true);
  };

  const cancelEditName = () => {
    setIsEditingName(false);
    setNameSaveError('');
  };

  const savePatientName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || !currentStudyUID) {
      return;
    }
    const hospitalId = getHospitalId();
    if (!hospitalId) {
      return;
    }
    setIsSavingName(true);
    setNameSaveError('');
    try {
      const res = await fetch(REPORTS.UPDATE_PATIENT_NAME, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          studyInstanceUID: currentStudyUID,
          patientNameOverride: trimmed,
          hospitalId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setNameSaveError(data?.msg || 'Failed to update patient name');
        return;
      }
      setPatientNameOverride(trimmed);
      setIsEditingName(false);
    } catch {
      setNameSaveError('Failed to update patient name');
    } finally {
      setIsSavingName(false);
    }
  };

  return (
    <>
      <div
        className="sticky top-0 z-20"
        style={{ background: '#041527', padding: '6px 14px 0' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            columnGap: 14,
            rowGap: 4,
            flexWrap: 'wrap',
            minHeight: 46,
          }}
        >
          <BackButton />
          {/* Patient lockup: name over study meta, typographic — no card, no box */}
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                color: '#E6EEF6',
                fontSize: '0.95rem',
                fontWeight: 600,
                letterSpacing: '0.01em',
                lineHeight: 1.2,
              }}
            >
              {isEditingName ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexWrap: 'wrap',
                  }}
                >
                  <input
                    type="text"
                    value={nameInput}
                    autoFocus
                    onChange={e => setNameInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        savePatientName();
                      }
                      if (e.key === 'Escape') {
                        cancelEditName();
                      }
                    }}
                    disabled={isSavingName}
                    style={{
                      background: 'transparent',
                      border: '1px solid rgba(255,255,255,0.5)',
                      borderRadius: '4px',
                      color: '#fff',
                      padding: '2px 8px',
                      fontSize: 'inherit',
                      minWidth: '180px',
                    }}
                  />
                  <button
                    type="button"
                    onClick={savePatientName}
                    disabled={isSavingName || !nameInput.trim()}
                    title="Save"
                    style={{ color: '#4ade80', fontWeight: 700, padding: '0 4px' }}
                  >
                    {isSavingName ? '…' : '✓'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEditName}
                    disabled={isSavingName}
                    title="Cancel"
                    style={{ color: '#f87171', fontWeight: 700, padding: '0 4px' }}
                  >
                    ✕
                  </button>
                  {nameSaveError && (
                    <span style={{ color: '#f87171', fontSize: '12px' }}>{nameSaveError}</span>
                  )}
                </span>
              ) : (
                <>
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {patientNameOverride || studyData?.patientName}
                  </span>
                  <button
                    type="button"
                    className="dt-icon-btn"
                    onClick={startEditName}
                    title="Edit patient name"
                    aria-label="Edit patient name"
                    style={{ width: 24, height: 24 }}
                  >
                    <PencilGlyph />
                  </button>
                </>
              )}
            </div>
            <div className="dt-meta">
              {studyData?.modalities && <span>{studyData?.modalities}</span>}
              <span>MRN {studyData?.mrn}</span>
              <span>{formatDate(studyData?.date)}</span>
              <span>{formatTime(studyData?.time)}</span>
            </div>
          </div>
          <div
            className="flex flex-wrap items-center"
            style={{ marginLeft: 'auto', gap: 4 }}
          >
            <div
              className="relative"
              ref={templateDropdown}
            >
              {isPreparingReport ? (
                <button
                  type="button"
                  className="dt-primary-btn"
                  disabled
                  aria-live="polite"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    opacity: 0.75,
                    cursor: 'progress',
                  }}
                >
                  <span
                    className="dt-btn-spinner"
                    aria-hidden="true"
                  />
                  Preparing report…
                </button>
              ) : resolvedReportId ? (
                <button
                  onClick={handleView}
                  className="dt-primary-btn"
                  disabled={isViewLoading}
                >
                  View Report
                </button>
              ) : (
                <div className="relative">
                  <button
                    onClick={() => {
                      if (isButtonDisabled || isLoading) {
                        return;
                      }
                      if (templateList.length > 0) {
                        setIsOpen(!isOpen);
                        return;
                      }
                      handleGetReportTemplate();
                      setIsOpen(!isOpen);
                    }}
                    disabled={isButtonDisabled}
                    className="dt-primary-btn"
                    style={{ width: '11rem', justifyContent: 'space-between' }}
                  >
                    <div className="white-space-nowrap overflow-hidden truncate text-ellipsis">
                      {isLoading ? (
                        <div className="flex items-center justify-center py-2">
                          <svg
                            className="h-5 w-5 animate-spin text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8H4z"
                            ></path>
                          </svg>
                        </div>
                      ) : (
                        <p>{selectedItem}</p>
                      )}
                    </div>
                    <Icon
                      name="chevron-down"
                      className="flex-shrink-0"
                    />
                  </button>
                  {isOpen && (
                    <div className="dt-template-menu">
                      <input
                        type="text"
                        className="dt-template-search"
                        placeholder="Search templates…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                      />
                      {/* Loader */}
                      {loading ? (
                        <div className="flex items-center justify-center py-2">
                          <svg
                            className="h-5 w-5 animate-spin text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8H4z"
                            ></path>
                          </svg>
                        </div>
                      ) : (
                        <ul className="dt-template-list">
                          {(() => {
                            const items = templateList?.data?.filter(item =>
                              item?.templateName?.toLowerCase()?.includes(searchTerm.toLowerCase())
                            );
                            if (!items?.length) {
                              return <li className="dt-template-empty">No templates found</li>;
                            }
                            return items.map((item, index) => (
                              <li
                                key={index}
                                className={`dt-template-item ${
                                  item.templateName === selectedItem ? 'is-selected' : ''
                                }`}
                                onClick={() => handleSelect(item)}
                              >
                                {item.templateName}
                              </li>
                            ));
                          })()}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            {canRegenerate && (
              <button
                type="button"
                onClick={() => requestRegenerate(currentReport)}
                className="dt-icon-btn"
                disabled={isLoading}
                aria-label="Regenerate report"
              >
                <Tooltip
                  content={'Regenerate report'}
                  position="bottom-right"
                >
                  <AutorenewRoundedIcon sx={{ fontSize: 18 }} />
                </Tooltip>
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                navigate(`/distribution/${currentStudyUID}`, {
                  state: {
                    reportId: resolvedReportId || undefined,
                    studyData: {
                      ...studyData,
                      name: studyData?.patientName ?? currentReport?.name,
                      patientName: studyData?.patientName ?? currentReport?.name,
                      dob: currentReport?.dob ?? studyData?.dob,
                      sex: currentReport?.sex,
                      age: currentReport?.age,
                      patientId: currentReport?.patientId,
                      reportName: currentReport?.reportName,
                      date: currentReport?.date,
                      time: currentReport?.time,
                      studyDate: currentReport?.studyDate,
                      studyTime: currentReport?.studyTime,
                      modality: currentReport?.modality,
                      reportAssignedTo: currentReport?.reportAssignedTo,
                      status: currentReport?.status,
                    },
                  },
                })
              }
              className="dt-icon-btn"
            >
              <Tooltip
                content={'Distribution'}
                position="bottom-right"
              >
                <DistributionGlyph />
              </Tooltip>
            </button>
            {/* Documents (scanned referrals, prior PDFs, CardioSoft XML) — the same
                study attachments the studies list manages. Clips are deliberately NOT
                here: this paperclip looks identical to the attachments button on every
                other screen, so pointing it at clips was misleading. Clips stay on the
                dedicated control at the foot of the report.
                Gated on the study, not the report, because an attachment belongs to the
                study and is often filed before anyone drafts a report. */}
            {currentStudyUID && (
              <button
                type="button"
                onClick={() => setIsAttachmentsOpen(true)}
                className="dt-icon-btn"
              >
                <Tooltip
                  content={'Attachments'}
                  position="bottom-right"
                >
                  <AttachGlyph />
                </Tooltip>
              </button>
            )}
            {currentStudyUID && dicomViewerEnabled && (
              <button
                type="button"
                onClick={() => navigate(`/viewer?StudyInstanceUIDs=${currentStudyUID}`)}
                className="dt-icon-btn"
                aria-label="Open in DICOM viewer"
              >
                <Tooltip
                  content={'Open in DICOM viewer'}
                  position="bottom-right"
                >
                  <OpenInNewRoundedIcon sx={{ fontSize: 18 }} />
                </Tooltip>
              </button>
            )}
            {(currentReport as any)?._id && (currentReport as any)?.reportAssignedToId && (
              <button
                onClick={() => {
                  setNotifResult(null);
                  setIsNotifModalOpen(true);
                }}
                className="dt-icon-btn"
              >
                <Tooltip
                  content={'Send Notification'}
                  position="bottom-right"
                >
                  <BellGlyph />
                </Tooltip>
              </button>
            )}
            {currentReport?.status &&
              canChangeReportStatus(userRole) &&
              (() => {
                const userId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
                const userName = sessionStorage.getItem('name') || localStorage.getItem('name');
                const idMatch =
                  currentReport?.reportAssignedToId &&
                  userId &&
                  String(userId) === String(currentReport.reportAssignedToId);
                const nameMatch =
                  currentReport?.reportAssignedTo &&
                  userName &&
                  userName.toLowerCase() === currentReport.reportAssignedTo.toLowerCase();
                return idMatch || nameMatch;
              })() && (
                <div
                  ref={statusDropdownRef}
                  className="relative"
                >
                  <button
                    onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                    disabled={isUpdatingStatus}
                    className="dt-status-pill"
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: 99,
                        flexShrink: 0,
                        background:
                          String(currentReport.status).toLowerCase() === 'finalized'
                            ? '#14ae5c'
                            : String(currentReport.status).toLowerCase() === 'preliminary'
                              ? '#e88930'
                              : '#dc9b98',
                      }}
                    />
                    {isUpdatingStatus ? 'Updating…' : currentReport.status}
                    <Icon
                      name="chevron-down"
                      className="text-white"
                    />
                  </button>

                  {isStatusDropdownOpen && (
                    <div
                      className="bg-secondary-dark absolute right-0 z-10 mt-1 rounded-md shadow-lg"
                      style={{ minWidth: '10rem' }}
                    >
                      <ul className="max-h-60 overflow-auto rounded-md py-1 text-base focus:outline-none sm:text-sm">
                        {statusOptions
                          .filter(
                            option =>
                              option.value.toLowerCase() !==
                              String(currentReport.status).toLowerCase()
                          )
                          .map(option => (
                            <div
                              key={option.value}
                              onClick={() => handleStatusChange(option.value)}
                              className="hover:bg-primary-dark hover:text-primary-light cursor-pointer py-2 pl-3 pr-9 text-white"
                            >
                              {option.label}
                            </div>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            <button
              onClick={() => toggleFullScreen()}
              className="dt-icon-btn"
            >
              <Tooltip
                content={'Full Screen'}
                position="bottom-right"
              >
                <ExpandGlyph />
              </Tooltip>
            </button>
          </div>
        </div>
        <EcgDivider />
      </div>
      {quickDistributeOpen && currentStudyUID && (
        <QuickDistributePopup
          studyInstanceUID={currentStudyUID}
          patientInfo={{
            name: studyData?.patientName ?? currentReport?.name,
            patientName: studyData?.patientName ?? currentReport?.name,
            dob: currentReport?.dob ?? studyData?.dob,
            reportName: currentReport?.reportName,
            date: currentReport?.date,
            time: currentReport?.time,
            patientId: currentReport?.patientId,
          }}
          onClose={() => setQuickDistributeOpen(false)}
        />
      )}

      {isNotifModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setIsNotifModalOpen(false)}
        >
          <div
            style={{
              background: '#1a2336',
              borderRadius: 10,
              padding: '28px 28px 24px',
              width: 420,
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: 16, color: '#e2e8f0' }}>
              Send Notification
            </h3>
            <textarea
              rows={4}
              placeholder="Type your message..."
              value={notifMessage}
              onChange={e => setNotifMessage(e.target.value)}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                background: '#0f1824',
                border: '1px solid #2d3f5a',
                borderRadius: 6,
                color: '#e2e8f0',
                padding: '10px 12px',
                fontSize: 14,
                resize: 'vertical',
              }}
            />
            {notifResult && (
              <p
                style={{
                  marginTop: 8,
                  fontSize: 13,
                  color: notifResult.ok ? '#4ade80' : '#f87171',
                }}
              >
                {notifResult.msg}
              </p>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn__secondary"
                onClick={() => setIsNotifModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn__primary"
                disabled={!notifMessage.trim() || isSendingNotif}
                onClick={async () => {
                  const reportId = (currentReport as any)?._id;
                  if (!reportId || !notifMessage.trim()) {
                    return;
                  }
                  setIsSendingNotif(true);
                  setNotifResult(null);
                  const result = await dispatch(
                    sendReportNotification({ reportId, message: notifMessage.trim() })
                  );
                  setIsSendingNotif(false);
                  if (sendReportNotification.fulfilled.match(result)) {
                    setNotifMessage('');
                    setNotifResult({ msg: 'Notification sent.', ok: true });
                    setTimeout(() => setIsNotifModalOpen(false), 1200);
                  } else {
                    setNotifResult({
                      msg: (result.payload as string) || 'Failed to send.',
                      ok: false,
                    });
                  }
                }}
              >
                {isSendingNotif ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
      {isAttachmentsOpen && currentStudyUID && (
        <PatientAttachmentsModal
          isAttachmentsModalOpen={isAttachmentsOpen}
          studyInstanceUID={currentStudyUID}
          onClose={() => setIsAttachmentsOpen(false)}
        />
      )}
      <Snackbar
        open={!!viewError}
        autoHideDuration={8000}
        onClose={() => setViewError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setViewError(null)}
          sx={{ maxWidth: 520 }}
        >
          {viewError}
        </Alert>
      </Snackbar>
      <RegenerateReportDialog {...regenerateDialogProps} />
    </>
  );
};

export default DetailsTile;
